import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AssetDto, GalleryDto } from '@picflow/types';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  concatMap,
  forkJoin,
  from,
  lastValueFrom,
  map,
  of,
  switchMap,
} from 'rxjs';
import { assetsPerResizeBatch, maxResizeBatchRetryCount, maxZipRequestSize } from '../consts';
import {
  Archive,
  ArchiveRequest,
  ArchiveRequestGallery,
  ArchiveSize,
  GALLERY_DOWNLOAD_SIZE,
} from '../interfaces/archive-request.interface';
import { copy } from '../lib/copy';
import { openFile } from '../lib/download-file';
import { estimateResizedAssetSize } from '../lib/estimate-resized-asset-size';
import { md5 } from '../lib/md5';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { AnalyticsService } from './analytics.service';
import { AssetService } from './asset.service';
import { GalleryPasswordService } from './gallery-password.service';
import { NetworkService } from './network.service';
import { SocketService } from './socket.service';
import { ToastService } from './toast.service';

export interface ArchiveItem {
  archive: Archive;
  progress: number;
  completed: boolean;
  downloaded: boolean;
  errored: boolean;
  cancelled: boolean;
  assetCount: number;
  key: string;
  partInfo?: { index: number; total: number };
}

@Injectable({
  providedIn: 'root',
})
export class ArchiveService {
  public items: ArchiveItem[] = [];

  private _updates: Subject<[string, string]> = new BehaviorSubject<[string, string]>(undefined);
  public readonly onUpdates: Observable<[string, string]> = this._updates.asObservable();

  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private galleryPasswordService: GalleryPasswordService,
    private socketService: SocketService,
    private assetService: AssetService,
    private networkService: NetworkService,
    private analytics: AnalyticsService,
    private toasts: ToastService
  ) {
    this.socketService.onSocketEvent.subscribe(event => {
      if (!event?.payload) return;

      const archive = (event.payload.archive || event.payload.Archive) as Archive;

      if (event.name === 'archive.finalized') {
        const item = this.items.find(i => i.archive.id === archive.id);
        if (!item) return console.warn('Got info about archive that we are not watching');
        item.archive = archive;
        this.onCompleted(item);
      }

      if (event.name === 'archive.finalized.fail') {
        const item = this.items.find(i => i.archive.id === archive.id);
        if (!item) return console.warn('Got info about archive that we are not watching');
        this.onErroritem(item);
      }

      if (event.name === 'archive.progress') {
        const payload = (event.payload.payload || event.payload.progress) as {
          // event.payload.payload can be removed after backend update
          id_archive: string;
          current: number;
          total: number;
        };

        const item = this.items.find(i => i.archive.id === payload.id_archive);
        if (!item) return console.warn('Got info about archive that we are not watching');
        const total = payload.total || payload.current;
        const current = payload.current as number;
        item.progress = (100 / total) * current;
      }
    });
  }

  public downloadArchive(item: ArchiveItem) {
    openFile(`${this.config.filesEndpoint}/${item.archive.download_url}`, item.archive.zip_name + '.zip');
    this.onDownloaded(item);
  }

  async onDownloaded(item: ArchiveItem) {
    this.analytics.track('archiveDownload', false, { size: parseInt(<string>(<unknown>item.archive.total_size)) });
    this._updates.next([item.archive.id, 'downloaded']);
    item.downloaded = true;
    await sleep(1000);
    this.items.splice(this.items.indexOf(item), 1);
    this.toasts.add('Archive downloading', 'success');
  }

  async onCompleted(item: ArchiveItem) {
    item.progress = 100;
    item.completed = true;
    this.analytics.track('archiveCompleted', false, { size: parseInt(<string>(<unknown>item.archive.total_size)) });
    this._updates.next([item.archive.id, 'complete']);

    if (this.items.length === 1 && !item.partInfo && !item.cancelled) {
      this.downloadArchive(item);
    }
  }

  async onErroritem(item: ArchiveItem) {
    item.progress = 0;
    item.errored = true;
    await sleep(5000);
    this.items.splice(this.items.indexOf(item), 1);
  }

  onCancelled(item: ArchiveItem) {
    item.cancelled = true;
    this.items.splice(this.items.indexOf(item), 1);
  }

  public cancelAll() {
    this.items = [];
  }

  public async requestArchiveCreation(
    params: ArchiveRequest,
    galleryId: string,
    assetCount: number,
    totalDownloadSize: number,
    partInfo?: { index: number; total: number }
  ): Promise<Archive> {
    const key = this.getZipKey(params);
    if (this.items.find(item => item.key === key)) return;

    const zip_parameters = params.zip_parameters.map(copy);

    const tempArchive = this.addtoArchiveItems(
      <Archive>{
        zip_name: params.zip_name,
        zip_parameters,
        size: params.size,
        number_items: assetCount,
        status: params.size === 'original' ? 'pending' : 'resizing',
        total_size: totalDownloadSize,
      },
      assetCount,
      partInfo
    );

    if (params.size !== 'original') {
      const assetUuids = params.zip_parameters.reduce((all, p) => [...all, ...(p.assetUuids || [])], []);
      const resp = await lastValueFrom(this.resizeAssets(assetUuids, params.size));
      if (!resp) {
        this.onErroritem(tempArchive);
        return;
      }
    }

    zip_parameters.forEach(p => {
      delete p.assetUuids;
    });

    const archive = <Archive>(
      await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/archives${this.getPassword(galleryId)}`, params))
    );

    archive.total_size = archive.total_size || totalDownloadSize;

    if (!archive) {
      this.items.splice(this.items.indexOf(tempArchive), 1);
      return;
    }
    this.updateArchive(tempArchive, archive);
    await lastValueFrom(
      this.socketService.sendMessage('archive.join', {
        id_archive: archive.id,
      })
    );
    return archive;
  }

  addtoArchiveItems(archive: Archive, assetCount: number, partInfo?: { index: number; total: number }) {
    const key = this.getZipKey(archive);
    const item: ArchiveItem = {
      archive,
      progress: 0,
      completed: false,
      errored: false,
      downloaded: false,
      cancelled: false,
      assetCount: assetCount,
      key,
      partInfo,
    };
    this.items.push(item);
    this._updates.next([item.archive.id, 'created']);
    return item;
  }

  async updateArchive(item: ArchiveItem, archive: Archive) {
    item.archive = archive;
    await sleep(1000); // give time for join message to be sent
    if (item.completed) return;

    // Check if the archive was completed before we could even subscribe
    const _archive = await this.getArchive(item.archive.id);
    if (_archive?.status === 'completed') {
      item.archive = _archive;
      this.onCompleted(item);
    }
    return item;
  }

  public async getArchive(id: string): Promise<Archive> {
    try {
      return <Archive>await lastValueFrom(this.http.get(`${this.config.apiEndpoint}/archives/${id}`));
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      console.error(err);
      return;
    }
  }

  public createArchiveRequestGallery(
    gallery: GalleryDto,
    items: string[] | null,
    zip_dir: string,
    assetUuids: string[]
  ): ArchiveRequestGallery {
    if (items && items.length === gallery.assets_count) items = undefined;
    return { gallery: gallery.id, zip_dir, items, assetUuids };
  }

  public createArchiveRequest(
    zipName: string,
    archiveRequestGalleries: ArchiveRequestGallery[],
    size: ArchiveSize
  ): ArchiveRequest {
    return {
      zip_name: zipName
        .trim()
        .split('\n')
        .join(' ')
        .replace(/[/\\#,+()$~%.'":*?<>{}]/g, ''),
      size,
      zip_parameters: archiveRequestGalleries.filter(r => !r.items || r.items.length),
    };
  }

  public getZipName(gallery: GalleryDto): string {
    if (gallery.name) return gallery.name;
    if (gallery.title) return gallery.title;
    if (gallery['tenant']?.name) return 'Gallery by ' + gallery['tenant'].name;
    return 'Picflow Gallery';
  }

  public async getMaxShortEdgeForGallery(galleryId: string) {
    const assets = await this.assetService.getAssetsForGallery(galleryId);
    return this.getMaxShortEdge(assets);
  }

  public getMaxShortEdge(assets: AssetDto[]) {
    let maxShortEdge = 0;
    assets.forEach(asset => {
      const vData = asset.version_data;
      if (!vData.data) return;
      const shortEdge = vData.data.width < vData.data.height ? vData.data.width : vData.data.height;
      maxShortEdge = Math.max(maxShortEdge, shortEdge);
    });
    return maxShortEdge;
  }

  public async onArchiveDone(archive: Archive) {
    if (!archive) return;
    return new Promise(resolve => {
      if (!this.items.find(a => a.archive.id === archive.id)) return;
      const sub = this.onUpdates.subscribe(events => {
        if (!events) return;
        if (events[0] !== archive.id) return;
        if (events[1] !== 'complete') return;
        sub?.unsubscribe();
        resolve(undefined);
      });
    });
  }

  public filterAssets(assets: AssetDto[], gallery: GalleryDto, size: ArchiveSize) {
    if (size !== 'original') {
      assets = assets.filter(a => {
        if (!a.version_data.data) return false;
        if (a.type !== 'image') return false;
        return true;
      });
    }
    if (!gallery.watermark || size !== 'original') {
      return assets;
    }

    // originals
    return assets.filter(asset => !asset.watermark_enabled);
  }

  public async createGalleryArchives(galleries: GalleryDto[], size: ArchiveSize) {
    const galleryDatas = await Promise.all(
      galleries.map(async gallery => {
        const assets = await this.assetService.getAssetsForGallery(gallery.id);
        return {
          gallery,
          assets: this.filterAssets(assets, gallery, size),
        };
      })
    );
    return this.createGalleryArchivesWithAssets(galleryDatas, size, false);
  }

  public async createGalleryArchivesWithAssets(
    galleryDatas: {
      gallery: GalleryDto;
      assets: AssetDto[];
    }[],
    size: ArchiveSize,
    includeAllAssets: boolean
  ) {
    const zipName = this.getZipName(galleryDatas[0].gallery);

    let requestSize = 0;
    let totalDownloadSize = 0;

    const requestDatasList: {
      gallery: GalleryDto;
      assetUuids: string[];
      assetIds: string[];
    }[][] = [];

    let requestDatas: {
      gallery: GalleryDto;
      assetUuids: string[];
      assetIds: string[];
    }[] = [];

    galleryDatas.forEach(data => {
      let requestData = { gallery: data.gallery, assetUuids: [], assetIds: [] };
      requestDatas.push(requestData);
      data.assets
        .filter(asset => {
          // if (asset.type !== 'image') return false;
          if (size === 'original') return true;
          if (!asset.version_data.data) return false;
          return true;
        })
        .forEach(asset => {
          if (size === 'original') {
            requestSize += asset.version_data.size;
            totalDownloadSize += asset.version_data.size;
          } else {
            const downloadSize = estimateResizedAssetSize(asset.version_data, GALLERY_DOWNLOAD_SIZE[size]);
            requestSize += downloadSize;
            totalDownloadSize += downloadSize;
          }

          const uuid = asset.watermark_enabled
            ? asset.version_data.watermark_uuid || asset.version_data.uuid
            : asset.version_data.uuid;
          requestData.assetUuids.push(uuid);
          requestData.assetIds.push(asset.id);

          if (requestSize > maxZipRequestSize && requestData.assetIds.length > 0) {
            requestSize = 0;
            requestDatasList.push(requestDatas);
            requestData = { gallery: data.gallery, assetUuids: [], assetIds: [] };
            requestDatas = [requestData];
          }
        });
    });

    requestDatasList.push(requestDatas);

    const filteredRequestDatasList = requestDatasList.filter(a => a.filter(b => b.assetUuids.length).length);

    const requests = filteredRequestDatasList
      .map((galleryDatas, index) => {
        const zipNameExtension =
          filteredRequestDatasList.length === 1 ? '' : ` ${index + 1} of ${filteredRequestDatasList.length}`;
        const request = this.createArchiveRequest(
          zipName + zipNameExtension,
          [
            ...galleryDatas.map(d =>
              this.createArchiveRequestGallery(
                d.gallery,
                includeAllAssets ? undefined : d.assetIds, // undefined to automatically include all assets when we are not splitting the zip into multiple parts
                `/${this.getZipName(d.gallery)}`,
                d.assetUuids
              )
            ),
          ],
          size
        );

        const assetCount = galleryDatas.reduce((t, g) => t + g.assetUuids.length, 0);

        return { request, assetCount };
      })
      .filter(a => a);

    return { requests, totalDownloadSize };
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '?password=' + encodeURIComponent(password);
  }

  private getZipKey(assetDownload: ArchiveRequest) {
    return md5(
      JSON.stringify({
        size: assetDownload.size,
        zip_name: assetDownload.zip_name,
        zip_parameters: assetDownload.zip_parameters,
      })
    );
  }

  private resizeAssets(uuids: string[], size: ArchiveSize) {
    const batches: string[][] = [];

    const batchCount = Math.ceil(uuids.length / assetsPerResizeBatch);

    for (let i = 0; i < batchCount; i++) {
      batches.push(uuids.slice(i * assetsPerResizeBatch, (i + 1) * assetsPerResizeBatch));
    }

    return from(batches).pipe(concatMap(batch => this.resizeAssetsBatch(batch, size)));
  }

  private resizeAssetsBatch(uuids: string[], size: ArchiveSize, retryCount = 0): Observable<boolean> {
    return forkJoin(uuids.map(p => this.resizeAsset(p, GALLERY_DOWNLOAD_SIZE[size]))).pipe(
      map(() => true),
      catchError(err => {
        console.error(err);
        if (retryCount >= maxResizeBatchRetryCount) return of(false);
        return this.networkService.online$.pipe(
          switchMap(() => {
            return this.resizeAssetsBatch(uuids, size, retryCount + 1);
          })
        );
      })
    );
  }

  private resizeAsset(uuid: string, size: number) {
    const url = `${this.config.assetsEndpoint}/images/resized/s${size}/${uuid}.jpg`;
    return from(fetch(url, { method: 'HEAD' }));
  }
}
